import React, { useState } from 'react';
import { Box, Chip } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { LoadCustomProcessor } from 'new/components/flows/shared/customProcessor';
import Constants from 'js/Constants';
import type { ByoCodeMode } from 'new/components/flows/types/byo/core/model';
import { NEXLA_UTILS_HELPERS } from 'new/components/flows/types/byo/v2/CodeEditor/nexlaUtils';
import classes from './studio.module.scss';

export interface StudioCodeEditorProps {
  code: string;
  onCodeChange: (code: string) => void;
  mode: ByoCodeMode;
  onModeChange: (mode: ByoCodeMode) => void;
  predefinedId: number | string | null;
  onPredefinedSelected: (id: number | string | null) => void;
  /** Hide the internal Write/Saved toggle when the parent owns function tabs. */
  showModeToggle?: boolean;
  /** Extra buttons rendered in the header bar to the left of Docs (e.g. Packages, Variables). */
  extraHeaderButtons?: React.ReactNode;
  'data-testid'?: string;
}

type DocsTab = 'Inputs' | 'Return value' | 'Logging' | 'Examples';
const DOC_TABS: DocsTab[] = ['Inputs', 'Return value', 'Logging', 'Examples'];

/**
 * Studio code editor — editor-first, with a clean `processor.py · Python`
 * header bar, a toggleable **Docs** side panel (the `process()` contract with
 * Inputs / Return value / Logging / Examples tabs), and a fullscreen toggle.
 * The code is the hero; Docs can be hidden to give the editor full width.
 * Reuses the existing Monaco editor + saved-processor picker unchanged.
 */
export function StudioCodeEditor({
  code,
  onCodeChange,
  mode,
  onModeChange,
  predefinedId,
  onPredefinedSelected,
  showModeToggle = true,
  extraHeaderButtons,
  'data-testid': dataTestId = 'byo-studio-code-editor',
}: StudioCodeEditorProps) {
  const [docsOpen, setDocsOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [docTab, setDocTab] = useState<DocsTab>('Inputs');

  const isPredefined = mode === 'predefined';

  return (
    <Box
      className={`${classes.codeEditor} ${fullscreen ? classes.codeEditorFull : ''}`}
      data-testid={dataTestId}
    >
      {/* Header bar */}
      <Box className={classes.codeHeaderBar}>
        <CodeIcon fontSize="small" className={classes.codeHeaderIcon} />
        <span className={classes.codeFileName}>processor.py</span>
        <Chip size="small" label="Python" className={classes.pyChip} />

        {showModeToggle ? (
          <Box className={classes.modeToggle}>
            <button
              type="button"
              className={`${classes.modeBtn} ${!isPredefined ? classes.modeBtnActive : ''}`}
              onClick={() => onModeChange('custom')}
              data-testid={`${dataTestId}-mode-custom`}
            >
              Write code
            </button>
            <button
              type="button"
              className={`${classes.modeBtn} ${isPredefined ? classes.modeBtnActive : ''}`}
              onClick={() => onModeChange('predefined')}
              data-testid={`${dataTestId}-mode-saved`}
            >
              Use saved
            </button>
          </Box>
        ) : null}

        <Box sx={{ flex: 1 }} />
        {extraHeaderButtons}
        {!isPredefined ? (
          <button
            type="button"
            className={`${classes.headerBtn} ${docsOpen ? classes.headerBtnActive : ''}`}
            onClick={() => setDocsOpen((v) => !v)}
            data-testid={`${dataTestId}-docs`}
          >
            <DescriptionOutlinedIcon fontSize="small" />
            Docs
          </button>
        ) : null}
        <button
          type="button"
          className={classes.headerBtn}
          onClick={() => setFullscreen((v) => !v)}
          aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          data-testid={`${dataTestId}-fullscreen`}
        >
          {fullscreen ? (
            <CloseFullscreenIcon fontSize="small" />
          ) : (
            <OpenInFullIcon fontSize="small" />
          )}
        </button>
      </Box>

      {/* Body: editor (+ optional Docs panel) */}
      {isPredefined ? (
        <Box className={classes.codeBodyPlain}>
          <LoadCustomProcessor
            sourceConfig={{ customProcessorId: predefinedId }}
            onSourceConfigChanged={(cfg: any) =>
              onPredefinedSelected(cfg?.customProcessorId ?? null)
            }
          />
        </Box>
      ) : (
        <Box className={classes.codeSplit}>
          <Box className={classes.editorPane} data-testid={`${dataTestId}-monaco`}>
            <LoadableMonacoEditor
              language={Constants.CodeLanguages.PYTHON}
              theme="dark"
              height="100%"
              value={code}
              onChange={onCodeChange}
              // automaticLayout: this editor stays mounted but `display:none`
              // while "Use Existing Function" is active (StudioCodeStep keeps
              // both function-tab panels mounted to avoid a remount race — see
              // its own comment). Monaco measures its container once at
              // creation and never re-measures on its own; without this, going
              // none→block on tab switch leaves it laid out at its hidden-time
              // 0×0 size — a blank editor. `automaticLayout` runs Monaco's own
              // periodic size check, which does re-measure on that transition.
              // Confirmed live 2026-08-21 (screenshot: toolbar renders, editor
              // body blank after switching tabs).
              options={{ automaticLayout: true }}
            />
          </Box>
          {docsOpen ? (
            <Box className={classes.docsPane} data-testid={`${dataTestId}-docs-panel`}>
              <div className={classes.docsTitle}>
                The <code>process()</code> contract
              </div>
              <div className={classes.docsTabs}>
                {DOC_TABS.map((t) => (
                  <button
                    type="button"
                    key={t}
                    className={`${classes.docsTab} ${t === docTab ? classes.docsTabActive : ''}`}
                    onClick={() => setDocTab(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className={classes.docsBody}>
                {docTab === 'Inputs' ? (
                  <>
                    <p>
                      <code>process()</code> receives three things:
                    </p>
                    <ul>
                      <li>
                        <code>file_path</code> — a path string, or a list of paths for multi-file
                        inputs.
                      </li>
                      <li>
                        <code>nexla_utils</code> — utility object. Use it for logging (see Logging
                        tab).
                      </li>
                      <li>
                        <code>**kwargs</code> — extra context, including:
                        <ul>
                          <li>
                            <code>file_type</code> — <code>'REPLICATION'</code> (raw file, default)
                            or <code>'PARSED'</code> (Nexla&apos;s parser already ran; input is
                            JSON). When it&apos;s <code>'PARSED'</code>, wrap each output record as{' '}
                            <code>{'{"rawMessage": record}'}</code> — unwrapped output breaks
                            downstream processing.
                          </li>
                          <li>
                            <code>nexlaMetaData</code> — per-record metadata dict (e.g.{' '}
                            <code>tags.display_path</code>, <code>sourceKey</code>) for resolving
                            the original filename.
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </>
                ) : null}
                {docTab === 'Return value' ? (
                  <>
                    <p>
                      Return a <strong>dict</strong> mapping output file names to a DataFrame or a
                      list of records:
                    </p>
                    <pre>{`return { "customers.csv": df }`}</pre>
                    <p>Each entry becomes one output file written to your destination.</p>
                  </>
                ) : null}
                {docTab === 'Logging' ? (
                  <>
                    <p>
                      Emit logs you&apos;ll see in the Test panel via <code>nexla_utils</code>:
                    </p>
                    <ul>
                      {NEXLA_UTILS_HELPERS.map((h) => (
                        <li key={h.signature}>
                          <code>{h.signature}</code> — {h.description}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {docTab === 'Examples' ? (
                  <pre>{`def process(file_path, nexla_utils, *args, **kwargs) -> dict:
    nexla_utils.publish_info_log(f"Processing {file_path}")
    import pandas as pd
    df = pd.read_json(file_path)
    df = df.dropna(subset=["email"])
    return {"customers.csv": df}`}</pre>
                ) : null}
              </div>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
}

export default StudioCodeEditor;
